import { useState } from "react";
import { useRegrasFiscais, useCstIcmsCombo, useCstPisCofinsCombo, useCClassTribCombo, useNaturezasOperacaoCombo, useCsosnCombo, useOrigensCombo, useCstsIpiCombo } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import { useDebounce } from "@/hooks/useDebounce";
import type { RegraFiscalListagemDto, RegraFiscalDto, SalvarRegraFiscalRequest } from "@/features/fiscal/types";
import { NcmSearchCombobox } from "@/components/NcmSearchCombobox";
import { CfopSearchCombobox } from "@/components/CfopSearchCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";

const UFS_BR = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const EMPTY_FORM: SalvarRegraFiscalRequest = {
  ncmId: 0, cfopId: 0, naturezaOperacaoId: null, naturezaReceitaId: null, crtId: null, ufDestino: "",
  cstIcmsId: null, csosnId: null, origemId: null, modBcIcms: null,
  aliqIcms: null, redBcIcms: null, aliqIcmsSt: null, mvaSt: null, fcp: null,
  cstPisId: null, cstCofinsId: null, aliqPis: null, aliqCofins: null,
  cstIpiId: null, aliqIpi: null,
  cClassTribId: null, aliqCbs: null, aliqIbsUf: null,
  observacao: "", isAtivo: true,
};

function dtoToForm(d: RegraFiscalDto): SalvarRegraFiscalRequest {
  return {
    ncmId: d.ncmId, cfopId: d.cfopId,
    naturezaOperacaoId: d.naturezaOperacaoId, naturezaReceitaId: d.naturezaReceitaId,
    crtId: d.crtId, ufDestino: d.ufDestino ?? "",
    cstIcmsId: d.cstIcmsId, csosnId: d.csosnId, origemId: d.origemId,
    modBcIcms: d.modBcIcms, aliqIcms: d.aliqIcms, redBcIcms: d.redBcIcms,
    aliqIcmsSt: d.aliqIcmsSt, mvaSt: d.mvaSt, fcp: d.fcp,
    cstPisId: d.cstPisId, cstCofinsId: d.cstCofinsId,
    aliqPis: d.aliqPis, aliqCofins: d.aliqCofins,
    cstIpiId: d.cstIpiId, aliqIpi: d.aliqIpi,
    cClassTribId: d.cClassTribId, aliqCbs: d.aliqCbs,
    aliqIbsUf: d.aliqIbsUf,
    observacao: d.observacao ?? "", isAtivo: d.isAtivo,
  };
}

function AliqInput({ label, value, onChange }: {
  label: string; value: number | null; onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number" min={0} max={100} step={0.01} placeholder="0,00"
        value={value ?? ""}
        onChange={e => { const v = parseFloat(e.target.value); onChange(isNaN(v) ? null : v); }}
      />
    </div>
  );
}

export default function RegraFiscal() {
  const [busca,          setBusca]          = useState("");
  const [ufFiltro,       setUfFiltro]       = useState("");
  const [pagina,         setPagina]         = useState(1);
  const [tamanhoPagina,  setTamanhoPagina]  = useState<TamanhoPagina>(20);
  const buscaDebounced = useDebounce(busca.trim(), 400);

  const { query, obterPorId, criar, atualizar, excluir } = useRegrasFiscais({
    busca:         buscaDebounced || undefined,
    ufDestino:     ufFiltro || undefined,
    pagina,
    tamanhoPagina,
  });
  const cstIcmsCombo    = useCstIcmsCombo();
  const cstPisCombo     = useCstPisCofinsCombo();
  const cClassTribCombo = useCClassTribCombo();
  const natOpCombo      = useNaturezasOperacaoCombo();
  const csosnCombo      = useCsosnCombo();
  const origensCombo    = useOrigensCombo();
  const cstsIpiCombo    = useCstsIpiCombo();

  const itens: RegraFiscalListagemDto[] = query.data?.itens ?? [];
  const totalItens  = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem   = Math.min(pagina * tamanhoPagina, totalItens);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [excluirId,  setExcluirId]  = useState<number | null>(null);
  const [editingId,  setEditingId]  = useState<number | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [form,       setForm]       = useState<SalvarRegraFiscalRequest>(EMPTY_FORM);
  const [ncmLabel,   setNcmLabel]   = useState("");

  const set = <K extends keyof SalvarRegraFiscalRequest>(k: K, v: SalvarRegraFiscalRequest[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setNcmLabel(""); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    setLoading(true);
    try { const dto = await obterPorId(id); setEditingId(id); setForm(dtoToForm(dto)); setNcmLabel(""); setDialogOpen(true); }
    catch { toast.error("Não foi possível carregar a regra fiscal."); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.ncmId || !form.cfopId) { toast.error("NCM e CFOP são obrigatórios."); return; }
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Regra fiscal atualizada.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Regra fiscal criada.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try { await excluir.mutateAsync(excluirId); toast.success("Regra excluída."); }
    catch { /* global toast */ }
    setExcluirId(null);
  };

  const isSaving       = criar.isPending || atualizar.isPending;
  const cstIcmsList    = (cstIcmsCombo.data ?? []).filter(c => c.cst && c.cst.length <= 2); // CST ICMS tradicional (IBS/CBS tem 3)
  const cstPisList     = cstPisCombo.data     ?? [];
  const cClassTribList = cClassTribCombo.data ?? [];
  const natOpList      = natOpCombo.data      ?? [];
  const csosnList      = csosnCombo.data      ?? [];
  const origensList    = origensCombo.data    ?? [];
  const cstsIpiList    = cstsIpiCombo.data    ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Regras Fiscais
          </h1>
          <p className="text-muted-foreground">Configuração de impostos por NCM / CFOP</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nova Regra</Button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por NCM ou CFOP..."
            className="pl-9"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        <Select value={ufFiltro || "_all"} onValueChange={v => { setUfFiltro(v === "_all" ? "" : v); setPagina(1); }}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Todas UF" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todas UF</SelectItem>
            {UFS_BR.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>NCM</TableHead>
              <TableHead>CFOP</TableHead>
              <TableHead>UF Destino</TableHead>
              <TableHead className="text-right">Alíq. ICMS %</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
              </TableRow>
            ) : itens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {buscaDebounced || ufFiltro ? "Nenhuma regra fiscal encontrada para o filtro." : "Nenhuma regra fiscal cadastrada."}
                </TableCell>
              </TableRow>
            ) : itens.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono">{r.ncmCodigo ?? r.ncmId}</TableCell>
                <TableCell className="font-mono">{r.cfopCodigo ?? r.cfopId}</TableCell>
                <TableCell>{r.ufDestino || "Todos"}</TableCell>
                <TableCell className="text-right">{r.aliqIcms != null ? `${r.aliqIcms}%` : "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={r.isAtivo ? "success" : "secondary"}>
                    {r.isAtivo ? "Ativa" : "Inativa"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r.id)} disabled={loading}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(r.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Regra Fiscal" : "Nova Regra Fiscal"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="ident">
              <TabsList className="mb-4 flex-wrap">
                <TabsTrigger value="ident">Identificação</TabsTrigger>
                <TabsTrigger value="tributacao">Tributação</TabsTrigger>
                <TabsTrigger value="icms">ICMS</TabsTrigger>
                <TabsTrigger value="piscofins">PIS/COFINS</TabsTrigger>
                <TabsTrigger value="ibscbs">IPI / IBS/CBS</TabsTrigger>
              </TabsList>

              {/* ── Tab 1: Identificação — NCM + Class. Tributária + Observação ── */}
              <TabsContent value="ident" className="space-y-4">
                <div className="space-y-1">
                  <Label>NCM *</Label>
                  <NcmSearchCombobox
                    value={form.ncmId || undefined}
                    onChange={(codigoInterno, codigo, descricao) => {
                      set("ncmId", codigoInterno);
                      setNcmLabel(`${codigo} — ${descricao}`);
                    }}
                    placeholder={ncmLabel || "Buscar NCM por código ou descrição..."}
                  />
                </div>

                <div className="space-y-1">
                  <Label>Classificação Tributária (IBS/CBS)</Label>
                  <Select
                    value={form.cClassTribId ? String(form.cClassTribId) : "null"}
                    onValueChange={v => set("cClassTribId", v === "null" ? null : parseInt(v))}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecionar classificação" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="null">— Não definido —</SelectItem>
                      {cClassTribList.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.codCClassTrib} — {c.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="observacao">Observação</Label>
                  <Textarea id="observacao" rows={3} maxLength={200} value={form.observacao} onChange={e => set("observacao", e.target.value)} />
                </div>

                <div className="flex items-center gap-2">
                  <Switch id="isAtivo" checked={form.isAtivo} onCheckedChange={v => set("isAtivo", v)} />
                  <Label htmlFor="isAtivo">Regra Ativa</Label>
                </div>
              </TabsContent>

              {/* ── Tab 2: Tributação — CFOP + UF + Natureza Operação ── */}
              <TabsContent value="tributacao" className="space-y-4">
                <div className="space-y-1">
                  <Label>CFOP *</Label>
                  <CfopSearchCombobox
                    byId
                    value={form.cfopId || null}
                    onChange={v => set("cfopId", v ?? 0)}
                    placeholder="Buscar CFOP por código ou descrição..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>UF Destino</Label>
                    <Select value={form.ufDestino || "TODOS"} onValueChange={v => set("ufDestino", v === "TODOS" ? "" : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TODOS">Todos os estados</SelectItem>
                        {UFS_BR.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Natureza de Operação</Label>
                    <Select
                      value={form.naturezaOperacaoId ? String(form.naturezaOperacaoId) : "null"}
                      onValueChange={v => set("naturezaOperacaoId", v === "null" ? null : parseInt(v))}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecionar natureza" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {natOpList.map(n => (
                          <SelectItem key={n.id} value={String(n.id)}>{n.descricao}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* ── Tab 3: ICMS ───────────────────────────────────────── */}
              <TabsContent value="icms" className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>CST ICMS</Label>
                    <Select value={form.cstIcmsId ? String(form.cstIcmsId) : "null"} onValueChange={v => set("cstIcmsId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar CST" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {cstIcmsList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.cst} — {c.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>CSOSN (Simples)</Label>
                    <Select value={form.csosnId ? String(form.csosnId) : "null"} onValueChange={v => set("csosnId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar CSOSN" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {csosnList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.codigoCsosn} — {c.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Origem da Mercadoria</Label>
                    <Select value={form.origemId ? String(form.origemId) : "null"} onValueChange={v => set("origemId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar origem" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {origensList.map(o => (
                          <SelectItem key={o.id} value={String(o.id)}>{o.codigo} — {o.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <AliqInput label="Alíq. ICMS (%)"     value={form.aliqIcms}   onChange={v => set("aliqIcms", v)} />
                  <AliqInput label="Red. Base ICMS (%)"  value={form.redBcIcms}  onChange={v => set("redBcIcms", v)} />
                  <AliqInput label="Alíq. ICMS ST (%)"  value={form.aliqIcmsSt} onChange={v => set("aliqIcmsSt", v)} />
                  <AliqInput label="MVA ST (%)"          value={form.mvaSt}      onChange={v => set("mvaSt", v)} />
                  <AliqInput label="FCP (%)"             value={form.fcp}        onChange={v => set("fcp", v)} />
                </div>
              </TabsContent>

              {/* ── Tab 4: PIS/COFINS ─────────────────────────────────── */}
              <TabsContent value="piscofins" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>CST PIS</Label>
                    <Select value={form.cstPisId ? String(form.cstPisId) : "null"} onValueChange={v => set("cstPisId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar CST" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {cstPisList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.cst} — {c.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>CST COFINS</Label>
                    <Select value={form.cstCofinsId ? String(form.cstCofinsId) : "null"} onValueChange={v => set("cstCofinsId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar CST" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {cstPisList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.cst} — {c.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <AliqInput label="Alíq. PIS (%)"    value={form.aliqPis}    onChange={v => set("aliqPis", v)} />
                  <AliqInput label="Alíq. COFINS (%)" value={form.aliqCofins} onChange={v => set("aliqCofins", v)} />
                </div>
              </TabsContent>

              {/* ── Tab 5: IPI / IBS/CBS ──────────────────────────────── */}
              <TabsContent value="ibscbs" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>CST IPI</Label>
                    <Select value={form.cstIpiId ? String(form.cstIpiId) : "null"} onValueChange={v => set("cstIpiId", v === "null" ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Selecionar CST IPI" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">— Não definido —</SelectItem>
                        {cstsIpiList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.codigo} — {c.descricao?.substring(0, 40)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <AliqInput label="Alíq. IPI (%)"    value={form.aliqIpi}    onChange={v => set("aliqIpi", v)} />
                  <AliqInput label="Alíq. CBS (%)"    value={form.aliqCbs}    onChange={v => set("aliqCbs", v)} />
                  <AliqInput label="Alíq. IBS UF (%)" value={form.aliqIbsUf}  onChange={v => set("aliqIbsUf", v)} />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir regra fiscal</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
